import React, { useMemo } from 'react';
import { VariableSizeList as List } from 'react-window';

// Components
import EmojiPopupRow from './emojiList/EmojiPopupRow';
import EmojiPopupContentNoResults from './emojiList/EmojiPopupContentNoResults';
import EmojiPopupContentLoading from './emojiList/EmojiPopupContentLoading';
import PopupModeMenuSearchForm from '../../popupPanel/types/modeMenu/PopupModeMenuSearchForm';
import EmojiPopupSkinTonePicker from './components/EmojiPopupSkinTonePicker';

// Hooks
import useEmojiPopupData from './hooks/useEmojiPopupData';

// Utils
import { isTitleRow } from './emojiPopupUtil';

// Constants
import { DEFAULT_COLUMN_COUNT, EmojiPopupContentProps } from './emojiPopupConstants';
import { EmojisRowData } from '../emojiTypes';

import './TabletEmojiPopupContent.scss';

const ROW_HEIGHT_GU = 34 / 10;
const LAST_ROW_IN_SECTION_HEIGHT_GU = 46 / 10;
const FIRST_ROW_PADDING_GU = 15 / 10;
const LAST_ROW_PADDING_GU = 25 / 10;

const PICKER_EMOJI_LIST_GU = ((32 + 4) * DEFAULT_COLUMN_COUNT) / 10;
const PICKER_LIST_WIDTH_GU = PICKER_EMOJI_LIST_GU + 48 / 10;
const PICKER_LIST_HEIGHT_GU = 400 / 10;

const getRowHeight = (gridSize: number) => (index: number, emojisRowData: EmojisRowData) => {
    if (index === 0) return (ROW_HEIGHT_GU + FIRST_ROW_PADDING_GU) * gridSize;
    if (index === emojisRowData.length - 1) return (ROW_HEIGHT_GU + LAST_ROW_PADDING_GU) * gridSize;

    const nextRow = emojisRowData[index + 1];
    const height = nextRow && isTitleRow(nextRow) ? LAST_ROW_IN_SECTION_HEIGHT_GU : ROW_HEIGHT_GU;

    return height * gridSize;
};

const TabletEmojiPopupContent = ({ gridSize, selectedEmojiIds, onSelectEmoji, closePopup }: EmojiPopupContentProps) => {
    const getRowHeightCb = useMemo(() => getRowHeight(gridSize), [gridSize]);

    const {
        setSearchFilter,
        emojiData,
        emojiDataLoaded,
        emojisRowData,
        handleEmojiListScroll,
        listRef,
        getKeyOfItemAtIndex,
        getHeightOfItemAtIndex,
        handleClickEmoji,
        skinTone,
        dispatchUpdateSkinTone,
    } = useEmojiPopupData(onSelectEmoji, closePopup, getRowHeightCb);

    const pickerListWidth = PICKER_LIST_WIDTH_GU * gridSize;
    const pickerListHeight = PICKER_LIST_HEIGHT_GU * gridSize;

    return (
        <div className="TabletEmojiPopupContent EmojiPopupContent">
            <div className="emoji-popup-header">
                <PopupModeMenuSearchForm handleSearchChange={setSearchFilter} />
                <EmojiPopupSkinTonePicker
                    emojiData={emojiData}
                    skinTone={skinTone}
                    setSkinTone={dispatchUpdateSkinTone}
                    gridSize={gridSize}
                />
            </div>

            {emojiDataLoaded && emojisRowData.length ? (
                <List
                    className="emoji-list"
                    onScroll={handleEmojiListScroll}
                    ref={listRef}
                    width={pickerListWidth}
                    height={pickerListHeight}
                    itemCount={emojisRowData.length}
                    itemSize={getHeightOfItemAtIndex}
                    itemKey={getKeyOfItemAtIndex}
                    itemData={{
                        rows: emojisRowData,
                        skinTone,
                        selectedEmojiIds,
                        handleClickEmoji,
                        gridSize,
                    }}
                    overscanCount={10}
                >
                    {EmojiPopupRow}
                </List>
            ) : emojiDataLoaded ? (
                <EmojiPopupContentNoResults
                    style={{ width: pickerListWidth, height: pickerListHeight }}
                    gridSize={gridSize}
                />
            ) : (
                <EmojiPopupContentLoading style={{ width: pickerListWidth, height: pickerListHeight }} />
            )}
        </div>
    );
};

export default TabletEmojiPopupContent;
